import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'
import { markInAppNotificationRead } from '../api'
import type { InAppNotificationListResponse } from '../api'
import { notificationQueryKeys } from './notificationQueryKeys'
import { refreshNotificationsPulse } from './refreshNotificationsPulse'
import type { PulseSnapshot } from '../utils/pulseUtils'

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (notificationId: string) =>
      markInAppNotificationRead(notificationId),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: notificationQueryKeys.list() })

      const previousList = queryClient.getQueriesData<
        InfiniteData<InAppNotificationListResponse>
      >({ queryKey: notificationQueryKeys.list() })

      let wasUnread = false
      const readAt = new Date().toISOString()

      queryClient.setQueriesData<InfiniteData<InAppNotificationListResponse>>(
        { queryKey: notificationQueryKeys.list() },
        (current) => {
          if (!current) {
            return current
          }
          return {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              data: page.data.map((notification) => {
                if (notification.id !== notificationId) {
                  return notification
                }
                if (!notification.readAt) {
                  wasUnread = true
                }
                return { ...notification, readAt }
              }),
            })),
          }
        }
      )

      const previousPulse = queryClient.getQueryData<PulseSnapshot>(
        notificationQueryKeys.pulse()
      )

      if (wasUnread) {
        queryClient.setQueryData<PulseSnapshot>(
          notificationQueryKeys.pulse(),
          (current) => ({
            unreadCount: Math.max(0, (current?.unreadCount ?? 0) - 1),
            headId: current?.headId ?? null,
          })
        )
      }

      return { previousList, previousPulse, wasUnread }
    },
    onError: (_error, _notificationId, context) => {
      if (context?.previousList) {
        for (const [queryKey, data] of context.previousList) {
          queryClient.setQueryData(queryKey, data)
        }
      }
      if (context?.previousPulse) {
        queryClient.setQueryData(
          notificationQueryKeys.pulse(),
          context.previousPulse
        )
      }
    },
    onSuccess: async () => {
      await refreshNotificationsPulse(queryClient)
    },
  })
}
